'use client'

import React, { useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Menu } from '../../@types/Menu'
import SimpleNavButton from './SimpleNavButton'
import ComplexNavButton from './ComplexNavButton'
import { cn } from '@/lib/utils'

interface MainNavProps {
  menus: Menu[]
}

export default function MainNav({ menus }: MainNavProps) {
  const [scrollLeft, setScrollLeft] = useState(0)
  const menuContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (menuContainerRef.current) {
        setScrollLeft(menuContainerRef.current.scrollLeft)
      }
    }

    const menuContainer = menuContainerRef.current
    if (menuContainer) {
      menuContainer.addEventListener('scroll', handleScroll)
      const initializeScroll = () => {
        if (menuContainer.scrollWidth > menuContainer.clientWidth) {
          const maxScroll = menuContainer.scrollWidth - menuContainer.clientWidth
          menuContainer.scrollLeft = -maxScroll
          if (menuContainer.scrollLeft === 0) {
            menuContainer.scrollTo({ left: maxScroll, behavior: 'auto' })
          }
        }
      }
      setTimeout(initializeScroll, 0)
      requestAnimationFrame(initializeScroll)
    }

    return () => {
      if (menuContainer) {
        menuContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const hasNestedSubmenu = (menu: Menu): boolean => {
    return false
    /* return Boolean(
      menu.industries?.some(industry => industry.families?.length > 0)
    ) */
  }

  const hasSimpleSubmenu = (menu: Menu): boolean => {
    return Boolean(
      menu.industries?.length ||
      menu.inventories?.length ||
      menu.parts_services?.length ||
      menu.tires_industries?.length ||
      menu.rents?.length ||
      menu.technological_solutions?.length ||
      menu.promotions?.length ||
      menu.brands?.length
    )
  }

  return (
    <nav className="px-8 hidden lg:flex items-center justify-between h-20 w-full">
      <div className="flex-shrink-0 pl-10 pr-4 [@media(min-width:1024px)_and_(max-width:1325px)]:pl-2">
        <Link prefetch={false} href="/">
          <Image
            src="/logo.svg"
            alt="Logo oficial de Matco - Soluciones y Maquinaria Pesada"
            width={180}
            height={45}
            className="hover:cursor-pointer [@media(min-width:1024px)_and_(max-width:1325px)]:w-[150px]"
            priority={true}
          />
        </Link>
      </div>

      <div className="flex-1 pr-10 overflow-hidden [@media(min-width:1024px)_and_(max-width:1325px)]:pr-2">
        <div
          ref={menuContainerRef}
          className={cn(
            "flex items-center gap-6",
            "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300"
          )}
          style={{ direction: 'rtl' }}
        >
          <div className="flex items-center gap-6 [@media(min-width:1024px)_and_(max-width:1080px)]:gap-5" style={{ direction: 'ltr' }}>
            {menus.map((menu, index) => (
              <div key={index} className="group flex-shrink-0">
                {hasNestedSubmenu(menu) ? (
                  <ComplexNavButton
                    menu={menu}
                    position={index < menus.length / 2 ? 'left' : 'right'}
                    scrollOffset={scrollLeft}
                  />
                ) : hasSimpleSubmenu(menu) ? (
                  <SimpleNavButton
                    menu={menu}
                    hasSubmenu
                    position={index < menus.length / 2 ? 'left' : 'right'}
                    scrollOffset={scrollLeft}
                  />
                ) : (
                  <SimpleNavButton menu={menu} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </nav>
  )
} 